import AppButton from '@nxsys/components/AppButton';
import {
  defaultBank,
  defaultHolidayPayCalcOptions,
  holidayPaymentMethodOptions,
  invoiceMethodOptions,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppConstants';
import {
  useGetAgencyBankById,
  useGetAgencyById,
  useGetAgencyInvoiceSettingById,
  useGetAgencyInvoicingById,
  useGetAgencyMarginById,
  useUpdateAgencyBankById,
  useUpdateAgencyById,
  useUpdateAgencyInvoiceSettingById,
  useUpdateAgencyInvoicingById,
  useUpdateAgencyMarginById,
} from '@nxsys/hooks/api/agency';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import {
  UpdateAgencyBankByIdParams,
  UpdateAgencyByIdParams,
  UpdateAgencyInvoiceSettingByIdParams,
  UpdateAgencyInvoicingByIdParams,
  UpdateAgencyMarginByIdParams,
} from '@nxsys/types/api';
import {
  defaultInvoiceMethodOptions,
  defaultInvoiceRaisedAfterOptions,
  defaultInvoiceRaisedMethodOptions,
  defaultInvoiceTemplateOptions,
  defaultMarginMethod,
  defaultPaymentTerm,
  defaultPayrollFreeMethod,
  defaultRuleInvoicingMethod,
  getAppConfig,
  getSalaryCalculationMethodOptions,
  getVATCodeOption,
  PAYMENT_MODEL_TEMPLATES,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Space } from 'antd';
import { some, toNumber } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PayrollFinanceForm, {
  PayrollFinanceFormRef,
  PayrollFinanceFormValues,
} from '../components/PayrollFinanceForm';
import AppLoader from '@nxsys/components/AppLoader';

const EditPayrollFinance = () => {
  const { messages } = useTypedIntl();

  const formRef = useRef<PayrollFinanceFormRef>(null);
  const params = useParams();
  const agencyId = params.agencyId;
  const dispatch = useDispatch<AppDispatch>();

  const defaultPaymentModel = getAppConfig('paymentModels');
  const hasPayeFTH = some(
    defaultPaymentModel,
    (item) => item.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
  );
  const salaryCalculationMethodOptions = getSalaryCalculationMethodOptions();

  const { goBack } = useGoBackByQuery();

  const handleGoBack = () =>
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: agencyId ? `/agency/${agencyId}` : '/agency',
    });

  const {
    run: fetchAgency,
    loading: isFetchingAgency,
    value: agencyValue,
  } = useGetAgencyById();
  const agencyData = useMemo(() => agencyValue?.data, [agencyValue]);

  const {
    run: fetchAgencyInvoicing,
    loading: isFetchingAgencyInvoicing,
    value: agencyInvoicingValue,
  } = useGetAgencyInvoicingById();
  const agencyInvoicingData = useMemo(
    () => agencyInvoicingValue?.data,
    [agencyInvoicingValue],
  );

  const {
    run: fetchAgencyInvoiceSetting,
    loading: isFetchingAgencyInvoiceSetting,
    value: agencyInvoiceSettingValue,
  } = useGetAgencyInvoiceSettingById();
  const agencyInvoiceSettingData = useMemo(
    () => agencyInvoiceSettingValue?.data,
    [agencyInvoiceSettingValue],
  );

  const {
    run: fetchAgencyMargin,
    loading: isFetchingAgencyMargin,
    value: agencyMarginValue,
  } = useGetAgencyMarginById();
  const agencyMarginData = useMemo(
    () => agencyMarginValue?.data,
    [agencyMarginValue],
  );

  const {
    run: fetchAgencyBank,
    value: agencyBankValue,
    loading: isFetchingAgencyBank,
  } = useGetAgencyBankById();
  const agencyBankData = useMemo(
    () => agencyBankValue?.data,
    [agencyBankValue],
  );

  const isFetchDataPending =
    isFetchingAgencyInvoiceSetting ||
    isFetchingAgencyMargin ||
    isFetchingAgency ||
    isFetchingAgencyInvoicing ||
    isFetchingAgencyBank;

  useEffect(() => {
    if (agencyId) {
      Promise.all([
        fetchAgencyInvoicing(agencyId),
        fetchAgencyInvoiceSetting(agencyId),
        fetchAgencyMargin(agencyId),
        fetchAgency(agencyId),
        fetchAgencyBank(agencyId),
      ]);
    }
  }, [agencyId]);

  const { run: updateAgencyInvoicing, loading: isUpdatingAgencyInvoicing } =
    useUpdateAgencyInvoicingById();
  const {
    run: updateAgencyInvoiceSetting,
    loading: isUpdatingAgencyInvoiceSetting,
  } = useUpdateAgencyInvoiceSettingById();
  const { run: updateAgencyMargin, loading: isUpdatingAgencyMargin } =
    useUpdateAgencyMarginById();
  const { run: updateAgency, loading: isUpdatingAgency } =
    useUpdateAgencyById();
  const { run: updateAgencyBank, loading: isUpdatingAgencyBank } =
    useUpdateAgencyBankById();

  const handleSubmit = async (values: PayrollFinanceFormValues) => {
    if (!agencyId) return;

    const invoicingParams: UpdateAgencyInvoicingByIdParams = {
      vatRegNo: values.invoicing.vatRegNo || '',
      vatRate: values.invoicing.vatRate?.value || null,
      domesticReverseChargeApplicable:
        values.invoicing.domesticReverseChargeApplicable ?? null,
      creditLimit: values.invoicing.creditLimit || '',
      creditTerm: values.invoicing.creditTerm || '',
      paymentTerm: values.invoicing.paymentTerm?.value || null,
      invoicingMethod: values.invoicing.invoicingMethod?.value || null,
      invoicingEmail: values.invoicing.invoicingEmail || '',
      ccEmail: values.invoicing.ccEmail || null,
    };

    const invoiceSettingParams: UpdateAgencyInvoiceSettingByIdParams = {
      raiseInvoice: values.invoiceSetting.raiseInvoice ?? false,
      // NX-6119: the PEO switch is hidden while ER cost is off but keeps its
      // value, so send false then.
      raiseInvoicePEO: !!(
        values.invoiceSetting.raiseInvoice &&
        values.invoiceSetting.raiseInvoicePEO
      ),
      invoiceRaisedAfter:
        values.invoiceSetting.invoiceRaisedAfter?.value ||
        'AFTER_CONFIRMED_PAYMENT',
      invoiceRaisedMethod:
        values.invoiceSetting.invoiceRaisedMethod?.value ||
        'RAISE_DIFFERENCE_ONLY',
      dueDate: toNumber(values.invoiceSetting.dueDate),
      rule: values.invoiceSetting.rule?.value || null,
      invoiceForSalary: values.invoiceSetting.invoiceForSalary || false,
      vatSalaryRate: values.invoiceSetting.vatSalaryRate ?? 0,
      vatSalaryRateCode: values.invoiceSetting.vatSalaryRateCode
        ? {
            label: values.invoiceSetting.vatSalaryRateCode.label,
            value: values.invoiceSetting.vatSalaryRateCode.value,
            VATRate:
              values.invoiceSetting.vatSalaryRateCode.fullData?.VATRate || '',
            VATCode:
              values.invoiceSetting.vatSalaryRateCode.fullData?.VATCode || '',
          }
        : null,
      invoiceForSalaryDescription:
        values.invoiceSetting.invoiceForSalaryDescription || '',
      invoiceForERNI: values.invoiceSetting.invoiceForERNI ?? false,
      vatERNIRate: values.invoiceSetting.vatERNIRate ?? 0,
      vatERNIRateCode: values.invoiceSetting.vatERNIRateCode
        ? {
            label: values.invoiceSetting.vatERNIRateCode.label,
            value: values.invoiceSetting.vatERNIRateCode.value,
            VATRate:
              values.invoiceSetting.vatERNIRateCode.fullData?.VATRate || '',
            VATCode:
              values.invoiceSetting.vatERNIRateCode.fullData?.VATCode || '',
          }
        : null,
      erNIPercent: values.invoiceSetting.erNIPercent ?? 0,
      invoiceForERNIDescription:
        values.invoiceSetting.invoiceForERNIDescription ?? null,
      invoiceERPension: values.invoiceSetting.invoiceERPension ?? false,
      vatERPensionRate: values.invoiceSetting.vatERPensionRate ?? 0,
      vatERPensionRateCode: values.invoiceSetting.vatERPensionRateCode
        ? {
            label: values.invoiceSetting.vatERPensionRateCode.label,
            value: values.invoiceSetting.vatERPensionRateCode.value,
            VATRate:
              values.invoiceSetting.vatERPensionRateCode.fullData?.VATRate ||
              '',
            VATCode:
              values.invoiceSetting.vatERPensionRateCode.fullData?.VATCode ||
              '',
          }
        : null,
      invoiceERPensionDescription:
        values.invoiceSetting.invoiceERPensionDescription ?? null,
      invoiceAppLevy: values.invoiceSetting.invoiceAppLevy ?? false,
      vatAppLevyRate: values.invoiceSetting.vatAppLevyRate ?? 0,
      vatAppLevyRateCode: values.invoiceSetting.vatAppLevyRateCode
        ? {
            label: values.invoiceSetting.vatAppLevyRateCode.label,
            value: values.invoiceSetting.vatAppLevyRateCode.value,
            VATRate:
              values.invoiceSetting.vatAppLevyRateCode.fullData?.VATRate || '',
            VATCode:
              values.invoiceSetting.vatAppLevyRateCode.fullData?.VATCode || '',
          }
        : null,
      invoiceAppLevyDescription:
        values.invoiceSetting.invoiceAppLevyDescription ?? null,
      invoicePayrollFee: values.invoiceSetting.invoicePayrollFee ?? false,
      vatPayrollFeeRate: values.invoiceSetting.vatPayrollFeeRate ?? 0,
      vatPayrollFeeRateCode: values.invoiceSetting.vatPayrollFeeRateCode
        ? {
            label: values.invoiceSetting.vatPayrollFeeRateCode.label,
            value: values.invoiceSetting.vatPayrollFeeRateCode.value,
            VATRate:
              values.invoiceSetting.vatPayrollFeeRateCode.fullData?.VATRate ||
              '',
            VATCode:
              values.invoiceSetting.vatPayrollFeeRateCode.fullData?.VATCode ||
              '',
          }
        : null,
      invoicePayrollFeeDescription:
        values.invoiceSetting.invoicePayrollFeeDescription ?? null,
      invoicingMethod: values.invoiceSetting.invoicingMethod?.value || null,
      feePerEmployee: values.invoiceSetting.feePerEmployee,
      tieredFees: (values.invoiceSetting.tieredFees as any) ?? null,
      invoiceTemplate:
        values.invoiceSetting.invoiceTemplate?.value || 'DEFAULT',
    };

    const marginParams: UpdateAgencyMarginByIdParams = {
      marginMethod: values.margin.marginMethod?.value || null,
      margin: values.margin.margin ? toNumber(values.margin.margin) : null,
      minMargin: values.margin.minMargin
        ? toNumber(values.margin.minMargin)
        : null,
      marginCapped: values.margin.marginCapped
        ? toNumber(values.margin.marginCapped)
        : null,
    };

    const agencyParams: UpdateAgencyByIdParams = {
      // NX-5555: tristate (null = inherit from company); lives on the agencies
      // table, not margin_agencies, so it rides the agency update not marginParams
      autoReduceMarginForNMW: values.margin.autoReduceMarginForNMW ?? null,
      holidayPayMethod: values.holidayPay.holidayPayMethod?.value ?? null,
      umbrellaHolidayPayMethod:
        values.holidayPay.umbrellaHolidayPayMethod?.value ?? null,
      payeHolidayPayRate: values.holidayPay.payeHolidayPayRate
        ? toNumber(values.holidayPay.payeHolidayPayRate)
        : null,
      umbrellaHolidayPayRate: values.holidayPay.umbrellaHolidayPayRate
        ? toNumber(values.holidayPay.umbrellaHolidayPayRate)
        : null,
      accrueEmploymentCosts: values.holidayPay.accrueEmploymentCosts ?? null,
      includeInTimesheetRate: values.holidayPay.includeInTimesheetRate ?? null,
      holidayPayCalculationRule:
        values.holidayPay.holidayPayCalculationRule?.value ?? null,
    };

    const trackingParams: UpdateAgencyByIdParams = {
      enableAWRTracking: values.tracking.enableAWRTracking ?? null,
      enableWTR: values.tracking.enableWTR ?? null,
      notificationWeeks: values.tracking.notificationWeeks
        ? [values.tracking.notificationWeeks]
        : null,
      takeHomePercent: values.tracking.takeHomePercent,
      salaryCalMethod: values.tracking.salaryCalMethod?.value || null,
      hoursCap: values.tracking.hoursCap || null,
      marginUmbrellaAdvanceAdjustment:
        values.tracking.marginUmbrellaAdvanceAdjustment ?? null,
    };

    const expenseParams: UpdateAgencyByIdParams = {
      isRequiredAttachmentExpenses:
        values.expense.isRequiredAttachmentExpenses ?? null,
      reclaimAppLevy: values.expense.reclaimAppLevy ?? null,
    };

    const bankParams: UpdateAgencyBankByIdParams = {
      bankName: values.paymentDetails.bankName?.value || '',
      accountName: values.paymentDetails.accountName || '',
      accountNumber: values.paymentDetails.accountNumber || '',
      sortCode: values.paymentDetails.sortCode || '',
      buildingSocietyNumber: values.paymentDetails.buildingSocietyNumber || '',
      IBANReference: values.paymentDetails.IBANReference || '',
      SWIFTNo: values.paymentDetails.SWIFTNo || '',
    };

    const restrictedHoursParams: UpdateAgencyByIdParams = {
      isRestrictedHours: values.restrictedHours.isRestrictedHours ?? null,
    };

    const communicationSettingsParams: UpdateAgencyByIdParams = {
      isSendEmailSelfBill:
        values.communicationSettings.isSendEmailSelfBill ?? null,
      isSendEmailP45: values.communicationSettings.isSendEmailP45 ?? null,
      isSendEmailP60: values.communicationSettings.isSendEmailP60 ?? null,
    };

    const payslipSettingsParams: UpdateAgencyByIdParams = {
      showHPEntitlement: values.payslipSetting.showHPEntitlement ?? null,
      showHPEmployerCostsYTD:
        values.payslipSetting.showHPEmployerCostsYTD ?? null,
    };

    const payslipTemplatesParams: UpdateAgencyByIdParams = {
      payePayslipTemplate:
        values.payslipTemplates.payePayslipTemplate?.value || null,
      umbrellaPayslipTemplate:
        values.payslipTemplates.umbrellaPayslipTemplate?.value || null,
      hybridPayslipTemplate:
        values.payslipTemplates.hybridPayslipTemplate?.value || null,
      hybridPayePayslipTemplate:
        values.payslipTemplates.hybridPayePayslipTemplate?.value || null,
      cisPayslipTemplate:
        values.payslipTemplates.cisPayslipTemplate?.value || null,
      ...(hasPayeFTH
        ? {
            payeETHPayslipTemplate:
              values.payslipTemplates.payeETHPayslipTemplate?.value || null,
          }
        : {}),
    };
    if ((values.payslipTemplates.logoUrl as File)?.name) {
      const res = await dispatch(
        commonActions.uploadFileToS3({
          file: values.payslipTemplates.logoUrl as File,
          // NX-6056: a payslip template image is fetched by URL by an
          // unauthenticated client (the emailed PDF renders it), so it has to
          // land under `public/*`. Without `purpose` it falls through to the
          // private branch and the payslip shows a broken image.
          purpose: 'logo',
        }),
      ).unwrap();
      payslipTemplatesParams.logoUrl = res.url;
    }
    if ((values.payslipTemplates.bannerUrl as File)?.name) {
      const res = await dispatch(
        commonActions.uploadFileToS3({
          file: values.payslipTemplates.bannerUrl as File,
          purpose: 'banner',
        }),
      ).unwrap();
      payslipTemplatesParams.bannerUrl = res.url;
    }

    Promise.all([
      updateAgencyInvoicing(agencyId, invoicingParams),
      updateAgencyInvoiceSetting(agencyId, invoiceSettingParams),
      updateAgencyMargin(agencyId, marginParams),
      updateAgency(agencyId, {
        ...agencyParams,
        ...trackingParams,
        ...expenseParams,
        ...restrictedHoursParams,
        ...communicationSettingsParams,
        ...payslipSettingsParams,
        ...payslipTemplatesParams,
      }),
      updateAgencyBank(agencyId, bankParams),
    ]).then(() => {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    });
  };

  const isUpdating =
    isUpdatingAgencyInvoicing ||
    isUpdatingAgencyInvoiceSetting ||
    isUpdatingAgencyMargin ||
    isUpdatingAgency ||
    isUpdatingAgencyBank;

  if (isFetchDataPending)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  return (
    <div className=" bg-white rounded-lg flex flex-col justify-center">
      <PayrollFinanceForm
        mode="update"
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={{
          invoicing: {
            vatRegNo: agencyInvoicingData?.vatRegNo ?? '',
            vatRate: agencyInvoicingData?.vatRate
              ? getVATCodeOption(agencyInvoicingData?.vatRate)
              : null,
            domesticReverseChargeApplicable:
              agencyInvoicingData?.domesticReverseChargeApplicable,
            creditLimit: agencyInvoicingData?.creditLimit ?? '',
            creditTerm: agencyInvoicingData?.creditTerm ?? '',
            paymentTerm: agencyInvoicingData?.paymentTerm
              ? defaultPaymentTerm.find(
                  (item) => item.value === agencyInvoicingData?.paymentTerm,
                )
              : null,
            invoicingMethod: agencyInvoicingData?.invoicingMethod
              ? defaultInvoiceMethodOptions.find(
                  (item) => item.value === agencyInvoicingData?.invoicingMethod,
                )
              : null,
            invoicingEmail: agencyInvoicingData?.invoicingEmail ?? '',
            ccEmail: agencyInvoicingData?.ccEmail ?? '',
          },
          invoiceSetting: {
            raiseInvoice: agencyInvoiceSettingData?.raiseInvoice ?? null,
            raiseInvoicePEO: agencyInvoiceSettingData?.raiseInvoicePEO ?? null,
            invoiceRaisedAfter:
              defaultInvoiceRaisedAfterOptions.find(
                (item) =>
                  item.value === agencyInvoiceSettingData?.invoiceRaisedAfter,
              ) || null,
            invoiceRaisedMethod:
              defaultInvoiceRaisedMethodOptions.find(
                (item) =>
                  item.value === agencyInvoiceSettingData?.invoiceRaisedMethod,
              ) || null,
            dueDate: agencyInvoiceSettingData?.dueDate?.toString() || '',
            rule:
              defaultRuleInvoicingMethod.find(
                (item) => item.value === agencyInvoiceSettingData?.rule,
              ) || null,
            invoiceForSalary:
              agencyInvoiceSettingData?.invoiceForSalary ?? null,
            vatSalaryRate: agencyInvoiceSettingData?.vatSalaryRate || 0,
            vatSalaryRateCode: agencyInvoiceSettingData?.vatSalaryRateCode
              ? {
                  label: agencyInvoiceSettingData.vatSalaryRateCode.label,
                  value: agencyInvoiceSettingData.vatSalaryRateCode.value,
                  fullData: {
                    VATRate: agencyInvoiceSettingData.vatSalaryRateCode.VATRate,
                    VATCode: agencyInvoiceSettingData.vatSalaryRateCode.VATCode,
                  },
                }
              : null,
            invoiceForSalaryDescription:
              agencyInvoiceSettingData?.invoiceForSalaryDescription ||
              'Salary Costs',
            invoiceForERNI: agencyInvoiceSettingData?.invoiceForERNI ?? null,
            erNIPercent: agencyInvoiceSettingData?.erNIPercent || 0,
            vatERNIRate: agencyInvoiceSettingData?.vatERNIRate || 0,
            vatERNIRateCode: agencyInvoiceSettingData?.vatERNIRateCode
              ? {
                  label: agencyInvoiceSettingData.vatERNIRateCode.label,
                  value: agencyInvoiceSettingData.vatERNIRateCode.value,
                  fullData: {
                    VATRate: agencyInvoiceSettingData.vatERNIRateCode.VATRate,
                    VATCode: agencyInvoiceSettingData.vatERNIRateCode.VATCode,
                  },
                }
              : null,
            invoiceForERNIDescription:
              agencyInvoiceSettingData?.invoiceForERNIDescription ||
              'ERNI Costs',
            invoiceERPension:
              agencyInvoiceSettingData?.invoiceERPension ?? null,
            vatERPensionRate: agencyInvoiceSettingData?.vatERPensionRate || 0,
            vatERPensionRateCode: agencyInvoiceSettingData?.vatERPensionRateCode
              ? {
                  label: agencyInvoiceSettingData.vatERPensionRateCode.label,
                  value: agencyInvoiceSettingData.vatERPensionRateCode.value,
                  fullData: {
                    VATRate:
                      agencyInvoiceSettingData.vatERPensionRateCode.VATRate,
                    VATCode:
                      agencyInvoiceSettingData.vatERPensionRateCode.VATCode,
                  },
                }
              : null,
            invoiceERPensionDescription:
              agencyInvoiceSettingData?.invoiceERPensionDescription ||
              'ER Pension Costs',
            invoiceAppLevy: agencyInvoiceSettingData?.invoiceAppLevy || null,
            vatAppLevyRate: agencyInvoiceSettingData?.vatAppLevyRate || 0,
            vatAppLevyRateCode: agencyInvoiceSettingData?.vatAppLevyRateCode
              ? {
                  label: agencyInvoiceSettingData.vatAppLevyRateCode.label,
                  value: agencyInvoiceSettingData.vatAppLevyRateCode.value,
                  fullData: {
                    VATRate:
                      agencyInvoiceSettingData.vatAppLevyRateCode.VATRate,
                    VATCode:
                      agencyInvoiceSettingData.vatAppLevyRateCode.VATCode,
                  },
                }
              : null,
            invoiceAppLevyDescription:
              agencyInvoiceSettingData?.invoiceAppLevyDescription ||
              'App levy Costs',
            invoicePayrollFee:
              agencyInvoiceSettingData?.invoicePayrollFee ?? null,
            vatPayrollFeeRate: agencyInvoiceSettingData?.vatPayrollFeeRate || 0,
            vatPayrollFeeRateCode:
              agencyInvoiceSettingData?.vatPayrollFeeRateCode
                ? {
                    label: agencyInvoiceSettingData.vatPayrollFeeRateCode.label,
                    value: agencyInvoiceSettingData.vatPayrollFeeRateCode.value,
                    fullData: {
                      VATRate:
                        agencyInvoiceSettingData.vatPayrollFeeRateCode.VATRate,
                      VATCode:
                        agencyInvoiceSettingData.vatPayrollFeeRateCode.VATCode,
                    },
                  }
                : null,
            invoicePayrollFeeDescription:
              agencyInvoiceSettingData?.invoicePayrollFeeDescription ||
              'Payroll Service Fee',
            invoicingMethod: agencyInvoiceSettingData?.invoicingMethod
              ? (defaultPayrollFreeMethod.find(
                  (item) =>
                    item.value === agencyInvoiceSettingData.invoicingMethod,
                ) as any) || undefined
              : undefined,
            feePerEmployee: agencyInvoiceSettingData?.feePerEmployee
              ? String(agencyInvoiceSettingData.feePerEmployee)
              : undefined,
            tieredFees: (agencyInvoiceSettingData?.tieredFees as any) || [],
            invoiceTemplate:
              defaultInvoiceTemplateOptions.find(
                (item) =>
                  item.value === agencyInvoiceSettingData?.invoiceTemplate,
              ) || null,
            invoiceMethod:
              invoiceMethodOptions.find(
                (item) =>
                  item.value === agencyInvoiceSettingData?.invoiceMethod,
              ) || null,
          },
          margin: {
            marginMethod: agencyMarginData?.marginMethod
              ? defaultMarginMethod.find(
                  (item) => item.value === agencyMarginData?.marginMethod,
                )
              : null,
            margin: agencyMarginData?.margin?.toString() ?? '',
            minMargin: agencyMarginData?.minMargin?.toString() ?? '',
            marginCapped: agencyMarginData?.marginCapped?.toString() ?? '',
            autoReduceMarginForNMW:
              (agencyData as any)?.autoReduceMarginForNMW ?? null,
          },
          holidayPay: {
            holidayPayMethod: agencyData?.holidayPayMethod
              ? holidayPaymentMethodOptions.find(
                  (item) => item.value === agencyData?.holidayPayMethod,
                )
              : null,
            umbrellaHolidayPayMethod: agencyData?.umbrellaHolidayPayMethod
              ? holidayPaymentMethodOptions.find(
                  (item) => item.value === agencyData?.umbrellaHolidayPayMethod,
                )
              : null,
            payeHolidayPayRate:
              agencyData?.payeHolidayPayRate?.toString() ?? '',
            umbrellaHolidayPayRate:
              agencyData?.umbrellaHolidayPayRate?.toString() ?? '',
            accrueEmploymentCosts: agencyData?.accrueEmploymentCosts,
            includeInTimesheetRate: agencyData?.includeInTimesheetRate,
            holidayPayCalculationRule: (agencyData as any)
              ?.holidayPayCalculationRule
              ? (defaultHolidayPayCalcOptions.find(
                  (item) =>
                    item.value ===
                    (agencyData as any)?.holidayPayCalculationRule,
                ) ?? null)
              : null,
          },
          tracking: {
            enableAWRTracking: agencyData?.enableAWRTracking,
            enableWTR: agencyData?.enableWTR,
            notificationWeeks:
              (agencyData as any)?.notificationWeeks?.[0]?.toString() ?? '',
            takeHomePercent: agencyData?.takeHomePayPercent?.toString() ?? '',
            salaryCalMethod: agencyData?.salaryCalMethod
              ? salaryCalculationMethodOptions.find(
                  (item) => item.value === agencyData?.salaryCalMethod,
                )
              : null,
            hoursCap: agencyData?.hoursCap?.toString() ?? '',
            marginUmbrellaAdvanceAdjustment:
              agencyData?.payeFthMarginAdjustment,
          },
          expense: {
            isRequiredAttachmentExpenses:
              agencyData?.isRequiredAttachmentExpenses,
            reclaimAppLevy: agencyData?.reclaimAppLevy,
          },
          paymentDetails: {
            bankName: agencyBankData?.bankName
              ? defaultBank.find(
                  (item) => item.value === agencyBankData?.bankName,
                )
              : null,
            accountName: agencyBankData?.accountName ?? '',
            accountNumber: agencyBankData?.accountNumber ?? '',
            sortCode: agencyBankData?.sortCode ?? '',
            buildingSocietyNumber: agencyBankData?.buildingSocietyNumber ?? '',
            IBANReference: agencyBankData?.IBANReference ?? '',
            SWIFTNo: agencyBankData?.SWIFTNo ?? '',
          },
          restrictedHours: {
            isRestrictedHours: agencyData?.isRestrictedHours,
          },
          communicationSettings: {
            isSendEmailSelfBill: agencyData?.isSendEmailSelfBill,
            isSendEmailP45: agencyData?.isSendEmailP45,
            isSendEmailP60: agencyData?.isSendEmailP60,
          },
          payslipSetting: {
            showHPEntitlement: agencyData?.showHPEntitlement,
            showHPEmployerCostsYTD: agencyData?.showHPEmployerCostsYTD,
          },
          payslipTemplates: {
            logoUrl: agencyData?.logoUrl ?? null,
            bannerUrl: agencyData?.bannerUrl ?? null,
            payePayslipTemplate:
              PAYMENT_MODEL_TEMPLATES.PAYE.find(
                (item) => item.value === agencyData?.payePayslipTemplate,
              ) ?? null,
            umbrellaPayslipTemplate:
              PAYMENT_MODEL_TEMPLATES.UMBRELLA.find(
                (item) => item.value === agencyData?.umbrellaPayslipTemplate,
              ) ?? null,
            hybridPayslipTemplate:
              PAYMENT_MODEL_TEMPLATES.HYBRID.find(
                (item) => item.value === agencyData?.hybridPayslipTemplate,
              ) ?? null,
            hybridPayePayslipTemplate:
              PAYMENT_MODEL_TEMPLATES.PAYE_HYBRID.find(
                (item) => item.value === agencyData?.hybridPayePayslipTemplate,
              ) ?? null,
            cisPayslipTemplate:
              PAYMENT_MODEL_TEMPLATES.CIS.find(
                (item) => item.value === agencyData?.cisPayslipTemplate,
              ) ?? null,
            ...(hasPayeFTH
              ? {
                  payeETHPayslipTemplate:
                    PAYMENT_MODEL_TEMPLATES.PAYE_FTH.find(
                      (item) =>
                        item.value === agencyData?.payeETHPayslipTemplate,
                    ) ?? null,
                }
              : {}),
          },
        }}
      />
      <div className="flex items-center justify-between mt-10">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel'] || 'Cancel'}
            onClick={handleGoBack}
            className="max-sm:hidden"
          />
          <AppButton
            type="primary"
            name={messages['common.save'] || 'Save'}
            onClick={() => {
              formRef.current?.submit();
            }}
            loading={isUpdating}
            disabled={isUpdating}
          />
        </Space>
      </div>
    </div>
  );
};

export default EditPayrollFinance;
